<template>
    <Dialog v-model:visible="visible" @update:visible="" modal closable
        class="w-full md:w-3/4 2xl:w-2/3 level-up-dialog" @show="startAnimation()">
        <template #header>
            <div />
        </template>
        <div class="relative overflow-hidden py-10">
            <!-- showing level progress-->
            <div v-if="showCelebration" class="absolute inset-0 pointer-events-none z-10">
                <div v-for="i in confettiPieces" :key="`left-${i}`" class="confetti-piece confetti absolute"
                    :style="getConfettiStyle(false, i)">
                </div>
                <div v-for="i in confettiPieces" :key="`right-${i}`" class="confetti-piece confetti absolute"
                    :style="getConfettiStyle(true, i)">
                </div>
            </div>

            <!-- Main content with celebration glow -->
            <div class="relative">
                <!-- Trophy or level icon with bounce animation -->
                <div class="flex justify-center mb-6">
                    <div class="text-6xl" :class="{ 'animate-bounce-gentle': showCelebration }">
                        🏆
                    </div>
                </div>

                <!-- Celebration message -->
                <div class="text-center animate-fade-in-up mb-8">
                    <p class="text-xl font-bold text-yellow-600 mb-2">🎉 {{ $t("congratulations") }} 🎉</p>
                    <p class="text-gray-600">{{ $t("levelUpMessage") }}</p>
                </div>

                <div class="space-y-6 mx-auto" v-if="mainLevelUp">
                    <!-- Main Level Section Header -->
                    <div class="text-center mb-4">
                        <div class="flex items-center justify-center space-x-2 mb-2">
                            <span class="text-2xl">⭐</span>
                            <h2 class="text-lg font-bold text-slate-800 dark:text-slate-200">{{ $t("overallLevel") }}
                            </h2>
                            <span class="text-2xl">⭐</span>
                        </div>
                        <div class="w-24 h-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full">
                        </div>
                    </div>

                    <LevelUpdateCard :levelUpdate="mainLevelUp!" showAnimation
                        :text="getMainLevelName(mainLevelUp!.newLevel!)" />
                </div>

                <div class="mx-auto mt-8" v-if="categoryLevelUps.length > 0">
                    <!-- Category Levels Section Header -->
                    <div class="text-center mb-4">
                        <div class="flex items-center justify-center space-x-2 mb-2">
                            <span class="text-2xl">🏅</span>
                            <h2 class="text-lg font-bold text-slate-800 dark:text-slate-200">{{ $t("categoryLevels") }}
                            </h2>
                            <span class="text-2xl">🏅</span>
                        </div>
                        <div class="w-24 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
                    </div>

                    <div class="grid grid-cols-1 gap-4" :class="{ 'md:grid-cols-2': categoryLevelUps.length > 1 }">
                        <LevelUpdateCard :levelUpdate="clu!.levelUpdate!" showAnimation v-for="clu in categoryLevelUps"
                            :key="clu!.categoryId!"
                            :text="getCategoryRankName(clu!.levelUpdate!.newLevel!, clu!.categoryId!, clu.categoryName!)" />
                    </div>
                </div>
            </div>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { useTaskBoardStore } from "@/stores";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from 'vue';
import LevelUpdateCard from "./LevelUpdateCard.vue";
import { useLevelNamer } from "@/composables/level-namer";

const confettiPieces = 120; // Total confetti pieces for each side

const { lastXpUpdate } = storeToRefs(useTaskBoardStore());

const visible = ref(false);


const mainLevelUp = computed(() => {
    const update = lastXpUpdate.value?.mainLevelUpdate;
    if (update?.newLevel != update?.oldLevel) {
        return update;
    }
    return null;
});

const categoryLevelUps = computed(() => {
    return lastXpUpdate.value?.categoryLevelUpdates
        .filter(lu => lu?.levelUpdate?.newLevel !== lu?.levelUpdate?.oldLevel) || [];
});

watch([mainLevelUp, categoryLevelUps], (newValue) => {
    const [mainUpdate, categoryLevelUps] = newValue;
    if (mainUpdate || categoryLevelUps.length > 0) {
        visible.value = true;
    } else {
        visible.value = false;
    }
}, { immediate: true });

const showCelebration = ref(false);
const { getMainLevelName, getCategoryRankName } = useLevelNamer();

const startAnimation = () => {
    showCelebration.value = true;

    setTimeout(() => {
        showCelebration.value = false;
    }, 4000); // Stop celebration after 4 seconds
}

const getConfettiStyle = (isRight: boolean, index: number) => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];
    const color = colors[index % colors.length];
    const delay = (index * 0.001) + 's'; // Faster succession for burst effect

    const angleSpread = 90; // 120 degree spread
    const minAngle = 0; // Start angle (pointing up-left)
    const angle = minAngle + (index / (confettiPieces - 1)) * angleSpread;

    const radian = (angle * Math.PI) / 180;
    const distance = 150 + Math.random() * 200; // Random distance for natural look
    const yOffset = Math.sin(radian) * distance * -1;
    const xOffset = Math.cos(radian) * distance * (isRight ? -1 : 1); // Invert for right side

    return {
        bottom: '20px',
        ...(isRight ? { right: '20px' } : { left: '20px' }),
        backgroundColor: color,
        animationDelay: delay,
        animationDuration: '2.5s',
        '--x-offset': `${xOffset}px`,
        '--y-offset': `${yOffset}px`
    }
}
</script>

<style scoped>
@keyframes confetti-shoot {
    0% {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg) scale(1);
    }

    50% {
        opacity: 1;
        transform: translate(var(--x-offset), var(--y-offset)) rotate(360deg) scale(0.8);
    }

    100% {
        opacity: 0;
        transform: translate(var(--x-offset), calc(var(--y-offset) + 100px)) rotate(720deg) scale(0.3);
    }
}

/* Fade in up animation */
@keyframes fade-in-up {
    0% {
        opacity: 0;
        transform: translateY(30px);
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Gentle bounce animation for trophy */
@keyframes bounce-gentle {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0) scale(1);
    }

    40% {
        transform: translateY(-10px) scale(1.1);
    }

    60% {
        transform: translateY(-5px) scale(1.05);
    }
}

/* Shine animation for progress bars */
@keyframes shine {
    0% {
        transform: translateX(-100%) skewX(-12deg);
    }

    100% {
        transform: translateX(200%) skewX(-12deg);
    }
}


.confetti-piece {
    width: 5px;
    height: 6px;
    position: absolute;
    border-radius: 2px;
}

.confetti {
    animation: confetti-shoot 2.5s ease-out forwards;
}

.animate-fade-in-up {
    animation: fade-in-up 1s ease-out;
}

.animate-bounce-gentle {
    animation: bounce-gentle 2s ease-in-out;
}

.animate-shine {
    animation: shine 2s ease-in-out 0.5s;
}
</style>

<style>
.level-up-dialog,
.level-up-dialog .p-dialog,
.level-up-dialog .p-dialog-mask {
    z-index: 10000 !important;
}
</style>